import { createSlice } from '@reduxjs/toolkit'

const STORAGE_KEY = 'crm-v2-ui'

const load = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : {}
  } catch {
    return {}
  }
}

// Only restore a pipeline id if it's a positive integer. Older builds (when
// the data layer was an in-memory store with string IDs like "pipes-sales-q2")
// could leave non-numeric values in localStorage that would 400 against the
// real backend's ParseIntPipe.
const restorePipelineId = (raw) => {
  const n = Number(raw)
  return Number.isInteger(n) && n > 0 ? n : null
}

const persisted = load()

const initialState = {
  selectedPipelineId: restorePipelineId(persisted.selectedPipelineId),
  view: persisted.view === 'list' ? 'list' : 'kanban',
  openDealDrawerId: null,
  openDealDrawerTab: null, // optional initial tab: 'resumen' | 'contactos' | 'notas' | 'actividades'
  filters: {
    q: '',
    ownerId: null,
    tagId: null,
    status: null, // 'open' | 'won' | 'lost' | null
    type: null,
  },
}

const persist = (state) => {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ selectedPipelineId: state.selectedPipelineId, view: state.view }),
    )
  } catch {
    // ignore quota/private-mode errors
  }
}

const crmUiSlice = createSlice({
  name: 'crmUi',
  initialState,
  reducers: {
    setSelectedPipeline: (state, action) => {
      state.selectedPipelineId = action.payload
      persist(state)
    },
    setView: (state, action) => {
      state.view = action.payload
      persist(state)
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    clearFilters: (state) => {
      state.filters = { q: '', ownerId: null, tagId: null, status: null, type: null }
    },
    openDealDrawer: (state, action) => {
      // Accepts either a bare id string or { id, tab? }
      if (action.payload && typeof action.payload === 'object') {
        state.openDealDrawerId = action.payload.id
        state.openDealDrawerTab = action.payload.tab || null
      } else {
        state.openDealDrawerId = action.payload
        state.openDealDrawerTab = null
      }
    },
    closeDealDrawer: (state) => {
      state.openDealDrawerId = null
      state.openDealDrawerTab = null
    },
  },
})

export const {
  setSelectedPipeline,
  setView,
  setFilters,
  clearFilters,
  openDealDrawer,
  closeDealDrawer,
} = crmUiSlice.actions

export const selectSelectedPipelineId = (state) => state.crmUi.selectedPipelineId
export const selectCrmView = (state) => state.crmUi.view
export const selectCrmFilters = (state) => state.crmUi.filters
export const selectOpenDealDrawerId = (state) => state.crmUi.openDealDrawerId
export const selectOpenDealDrawerTab = (state) => state.crmUi.openDealDrawerTab

export default crmUiSlice.reducer
